import React from "react";
import { Link } from "react-router-dom";
import "./BreadcrumbNav.css";

/**
 * BreadcrumbNav Component
 * 
 * Renders a clickable breadcrumb navigation trail.
 * Parent segments are clickable links, current page is plain text.
 * 
 * @param {Array} segments - Array of breadcrumb segments from useBreadcrumb hook
 *   Each segment: { name: string, path: string|null, isClickable: boolean, isLast: boolean }
 * @param {Function} onNavigate - Optional callback when navigating (for additional actions)
 * @param {string} separator - Separator between segments (default: " > ")
 * @param {string} className - Additional CSS class for the container
 */
const BreadcrumbNav = ({ 
  segments = [], 
  onNavigate,
  separator = " > ",
  className = ""
}) => {
  if (!segments || segments.length === 0) {
    return <span className="breadcrumb-text">Home</span>;
  }

  const handleClick = (segment, e) => {
    if (segment.isClickable && segment.path) {
      // Call optional callback for any additional actions (e.g., analytics)
      if (onNavigate) {
        onNavigate(segment);
      }
    }
  };

  return (
    <nav className={`breadcrumb-nav ${className}`} aria-label="Breadcrumb">
      <ol className="breadcrumb-list">
        {segments.map((segment, index) => (
          <li key={index} className="breadcrumb-item">
            {segment.isClickable && segment.path ? (
              // Clickable parent segment
              <Link
                to={segment.path}
                className="breadcrumb-link"
                onClick={(e) => handleClick(segment, e)}
                title={`Go to ${segment.name}`}
              >
                {segment.name}
              </Link>
            ) : (
              // Non-clickable segment (current page or no path)
              <span 
                className={`breadcrumb-text ${segment.isLast ? 'breadcrumb-current' : ''}`}
                aria-current={segment.isLast ? "page" : undefined}
              >
                {segment.name}
              </span>
            )}
            
            {/* Separator (except for last item) */}
            {!segment.isLast && (
              <span className="breadcrumb-separator" aria-hidden="true">
                {separator}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

/**
 * Simple text-based breadcrumb (for backward compatibility)
 * Just displays the path string without clickable links
 */
export const BreadcrumbText = ({ pagePath }) => {
  return (
    <span className="breadcrumb-text-simple">
      {pagePath || "Home"}
    </span>
  );
};

export default BreadcrumbNav;
